import { useQuery } from '@tanstack/react-query';
import api from './authService';

// Query Keys
export const vendorAnalyticsKeys = {
  all: ['vendor-analytics'],
  overview: (period) => [...vendorAnalyticsKeys.all, 'overview', period],
  performance: (period) => [...vendorAnalyticsKeys.all, 'performance', period],
  earnings: (period) => [...vendorAnalyticsKeys.all, 'earnings', period],
  reports: (type, period) => [...vendorAnalyticsKeys.all, 'reports', type, period],
  insights: (period) => [...vendorAnalyticsKeys.all, 'insights', period],
};

// API Functions
const vendorAnalyticsApi = {
  // Get vendor analytics overview
  getOverview: async (period = 'month') => {
    const response = await api.get(`/vendor/analytics`, {
      params: { period }
    });
    return response.data;
  },

  // Get vendor performance metrics
  getPerformance: async (period = 'month') => {
    const response = await api.get(`/vendor/analytics/performance`, {
      params: { period }
    });
    return response.data;
  },

  // Get vendor earnings
  getEarnings: async (period = 'month') => {
    const response = await api.get(`/vendor/analytics/earnings`, {
      params: { period }
    });
    return response.data;
  },

  // Get vendor reports
  getReports: async (type = 'bookings', period = 'month') => {
    const response = await api.get(`/vendor/reports`, {
      params: { type, period }
    });
    return response.data;
  },

  // Get vendor insights
  getInsights: async (period = 'month') => {
    const response = await api.get(`/vendor/analytics/insights`, {
      params: { period }
    });
    return response.data;
  },
};

// React Query Hooks

/**
 * Get vendor analytics overview
 * @param {string} period - Time period: 'week', 'month', 'quarter', 'year'
 * @param {object} options - React Query options
 */
export const useVendorAnalytics = (period = 'month', options = {}) => {
  return useQuery({
    queryKey: vendorAnalyticsKeys.overview(period),
    queryFn: () => vendorAnalyticsApi.getOverview(period),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Get vendor performance metrics
 * @param {string} period - Time period: 'week', 'month', 'quarter', 'year'
 * @param {object} options - React Query options
 */
export const useVendorPerformance = (period = 'month', options = {}) => {
  return useQuery({
    queryKey: vendorAnalyticsKeys.performance(period),
    queryFn: () => vendorAnalyticsApi.getPerformance(period),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Get vendor earnings
 * @param {string} period - Time period: 'week', 'month', 'quarter', 'year'
 * @param {object} options - React Query options
 */
export const useVendorEarnings = (period = 'month', options = {}) => {
  return useQuery({
    queryKey: vendorAnalyticsKeys.earnings(period),
    queryFn: () => vendorAnalyticsApi.getEarnings(period),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Get vendor reports
 * @param {string} type - Report type: 'bookings', 'revenue', 'clients', 'services'
 * @param {string} period - Time period: 'week', 'month', 'quarter', 'year'
 * @param {object} options - React Query options
 */
export const useVendorReports = (type = 'bookings', period = 'month', options = {}) => {
  return useQuery({
    queryKey: vendorAnalyticsKeys.reports(type, period),
    queryFn: () => vendorAnalyticsApi.getReports(type, period),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Get vendor insights
 * @param {string} period - Time period: 'week', 'month', 'quarter', 'year'
 * @param {object} options - React Query options
 */
export const useVendorInsights = (period = 'month', options = {}) => {
  return useQuery({
    queryKey: vendorAnalyticsKeys.insights(period),
    queryFn: () => vendorAnalyticsApi.getInsights(period),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export default vendorAnalyticsApi;

